/**
 * Per-chat folder attachment, backed by an app-wide folder library.
 *
 * The chat does not own a unique folder — it points at one of the entries
 * in the user's folder library (`folder-library.ts`). The hook exposes the
 * active entry plus the full library so a selector in the prompt toolbar
 * can render and switch folders without leaving the chat.
 *
 * On mount the hook also migrates any legacy chatId-keyed handle into the
 * library schema, so chats from before the redesign keep working.
 *
 * @module file-agent/lib/use-folder-attachment
 */

import { useCallback, useEffect, useState } from "react";
import { ensureReadWritePermission } from "./fs-tools";
import { isFileSystemAccessSupported } from "./browser-support";
import {
  clearActiveFolderId,
  getActiveFolderId,
  getLibrary,
  loadFolderHandle,
  migrateLegacyChatHandle,
  removeLibraryEntry,
  saveFolderHandle,
  setActiveFolderId,
  touchLibraryEntry,
  upsertLibraryEntry,
  type FolderLibraryEntry,
} from "./folder-library";

/** The shape returned to consumers of the hook. */
export interface FolderAttachment {
  /** True once we've finished checking storage on mount. */
  ready: boolean;
  /** True when this browser supports the File System Access API. */
  supported: boolean;
  /** The active handle, or null when no folder is attached to this chat. */
  handle: FileSystemDirectoryHandle | null;
  /** Display name of the active folder. */
  folderName: string | null;
  /**
   * True when the active folder needs the user to re-grant permission
   * (e.g. after a page reload). Call `reconnect()` from a user gesture.
   */
  needsReconnect: boolean;

  /** All folders the user has attached at least once. */
  library: FolderLibraryEntry[];
  /** Library id of the currently active folder for this chat. */
  activeFolderId: string | null;

  /** Open the system directory picker, attach the result, mark active. */
  attach: () => Promise<void>;
  /** Detach the active folder from this chat (keeps it in the library). */
  detach: () => Promise<void>;
  /** Re-request permission on the active handle (must run on user gesture). */
  reconnect: () => Promise<boolean>;
  /** Switch this chat to a different folder from the library. */
  selectFolder: (id: string) => Promise<void>;
  /** Remove a folder from the library entirely (and detach if active). */
  removeFolder: (id: string) => Promise<void>;
}

declare global {
  interface Window {
    showDirectoryPicker?: (opts?: {
      mode?: "read" | "readwrite";
    }) => Promise<FileSystemDirectoryHandle>;
  }
}

type Permission = "granted" | "denied" | "prompt";

async function queryPermission(
  handle: FileSystemDirectoryHandle,
): Promise<Permission> {
  return (handle as unknown as {
    queryPermission(opts: { mode: "readwrite" }): Promise<Permission>;
  }).queryPermission({ mode: "readwrite" });
}

/**
 * Optional behaviour switches for the hook.
 *
 * `allowSwap` lifts the per-scope lock so callers like the agent landing
 * page can keep changing their pick before a real chat exists. The chat
 * page leaves it false so once a real chat is bound, the folder is
 * permanent.
 *
 * `fallbackKey` makes the hook inherit the active folder from another
 * scope on first read. The chat page uses `agentId` as the fallback so a
 * folder picked on the landing page (stored under `agentId`) propagates
 * into the newly-created chat (stored under `chatId`) and then locks.
 */
export interface UseFolderAttachmentOptions {
  allowSwap?: boolean;
  fallbackKey?: string;
}

/**
 * Manage folder attachment for a single scope (chat id or agent id)
 * against the shared library. Returns `supported: false` on browsers
 * without the File System Access API.
 */
export function useFolderAttachment(
  key: string | undefined,
  options: UseFolderAttachmentOptions = {},
): FolderAttachment {
  const { allowSwap = false, fallbackKey } = options;
  const [supported] = useState(() => isFileSystemAccessSupported());
  const [ready, setReady] = useState(false);
  const [library, setLibrary] = useState<FolderLibraryEntry[]>([]);
  const [activeFolderId, setActiveFolderIdState] = useState<string | null>(null);
  const [handle, setHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [folderName, setFolderName] = useState<string | null>(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);

  const refreshLibrary = useCallback(() => {
    setLibrary(getLibrary());
  }, []);

  // Restore the scope's active folder. Three sources, in order:
  //   1. The folder explicitly stored under `key`.
  //   2. The legacy chatId-keyed handle (for chats that predate the
  //      library) — migrated forward into the library schema.
  //   3. The fallbackKey scope — copied into `key` on first read so
  //      subsequent loads come from `key` directly and the binding sticks.
  useEffect(() => {
    let cancelled = false;
    if (!supported || !key) {
      setReady(true);
      refreshLibrary();
      return;
    }
    (async () => {
      let activeId = getActiveFolderId(key);
      if (!activeId) {
        activeId = await migrateLegacyChatHandle(key);
      }
      if (!activeId && fallbackKey && fallbackKey !== key) {
        const inherited = getActiveFolderId(fallbackKey);
        if (inherited) {
          setActiveFolderId(key, inherited);
          activeId = inherited;
        }
      }
      if (cancelled) return;

      refreshLibrary();
      setActiveFolderIdState(activeId);

      if (activeId) {
        const loaded = await loadFolderHandle(activeId);
        if (cancelled) return;
        if (loaded) {
          setHandle(loaded);
          setFolderName(loaded.name);
          const perm = await queryPermission(loaded);
          if (cancelled) return;
          setNeedsReconnect(perm !== "granted");
        } else {
          // Library entry exists in localStorage but the handle was wiped
          // (e.g. the user cleared site data). Clear the stale pointer.
          clearActiveFolderId(key);
          setActiveFolderIdState(null);
        }
      }
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [key, supported, fallbackKey, refreshLibrary]);

  const attach = useCallback(async () => {
    if (!supported || !key || !window.showDirectoryPicker) return;
    // Without `allowSwap`, the binding is permanent: even a stale caller
    // can't silently overwrite it. The landing page passes allowSwap=true
    // so users can keep refining their pick before the chat is created.
    if (activeFolderId && !allowSwap) return;
    const picked = await window.showDirectoryPicker({ mode: "readwrite" });
    const entry = upsertLibraryEntry({ name: picked.name });
    await saveFolderHandle(entry.id, picked);
    setActiveFolderId(key, entry.id);

    setActiveFolderIdState(entry.id);
    setHandle(picked);
    setFolderName(picked.name);
    setNeedsReconnect(false);
    refreshLibrary();
  }, [activeFolderId, allowSwap, key, supported, refreshLibrary]);

  const detach = useCallback(async () => {
    if (!key) return;
    clearActiveFolderId(key);
    setActiveFolderIdState(null);
    setHandle(null);
    setFolderName(null);
    setNeedsReconnect(false);
  }, [key]);

  const reconnect = useCallback(async () => {
    if (!handle) return false;
    const ok = await ensureReadWritePermission(handle);
    setNeedsReconnect(!ok);
    return ok;
  }, [handle]);

  const selectFolder = useCallback(
    async (id: string) => {
      if (!key) return;
      // First-time selection only unless the caller explicitly opts in to
      // swap (e.g. agent landing page where no chat exists yet).
      if (activeFolderId && !allowSwap) return;
      const loaded = await loadFolderHandle(id);
      if (!loaded) {
        // The library entry has gone stale — drop it so the dropdown
        // doesn't keep offering a folder we can't reach.
        await removeLibraryEntry(id);
        refreshLibrary();
        return;
      }
      setActiveFolderId(key, id);
      touchLibraryEntry(id);

      setActiveFolderIdState(id);
      setHandle(loaded);
      setFolderName(loaded.name);

      const perm = await queryPermission(loaded);
      if (perm === "granted") {
        setNeedsReconnect(false);
      } else {
        // Re-request permission inline since the user just clicked the
        // entry — that's a user gesture the browser will accept.
        const ok = await ensureReadWritePermission(loaded);
        setNeedsReconnect(!ok);
      }
      refreshLibrary();
    },
    [activeFolderId, allowSwap, key, refreshLibrary],
  );

  const removeFolder = useCallback(
    async (id: string) => {
      await removeLibraryEntry(id);
      if (key && getActiveFolderId(key) === id) {
        clearActiveFolderId(key);
        setActiveFolderIdState(null);
        setHandle(null);
        setFolderName(null);
        setNeedsReconnect(false);
      }
      refreshLibrary();
    },
    [key, refreshLibrary],
  );

  return {
    ready,
    supported,
    handle,
    folderName,
    needsReconnect,
    library,
    activeFolderId,
    attach,
    detach,
    reconnect,
    selectFolder,
    removeFolder,
  };
}
